import React, { useState, useEffect } from 'react';
import { Check, Shield, Users, Beaker, FileText, Eye, X } from 'lucide-react';
import ProductGallery from '../../components/ProductGallery';

const Products: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCOAModal, setShowCOAModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    // Check if there's a hash in the URL (for COA link)
    if (window.location.hash === '#coa') {
      const coaSection = document.getElementById('coa');
      if (coaSection) {
        setTimeout(() => {
          coaSection.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else if (window.location.hash === '#gallery') {
      const gallerySection = document.getElementById('gallery');
      if (gallerySection) {
        setTimeout(() => {
          gallerySection.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Scroll to top for other navigation
      window.scrollTo(0, 0);
    }
  }, []);

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#coa') {
        const coaSection = document.getElementById('coa');
        if (coaSection) {
          setTimeout(() => {
            coaSection.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      } else if (window.location.hash === '#gallery') {
        const gallerySection = document.getElementById('gallery');
        if (gallerySection) {
          setTimeout(() => {
            gallerySection.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      } else {
        window.scrollTo(0, 0);
      }
    };

    // Also handle when user clicks on links while already on the page
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      if (link && link.getAttribute('href')?.includes('#coa')) {
        setTimeout(() => {
          const coaSection = document.getElementById('coa');
          if (coaSection) {
            coaSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else if (link && link.getAttribute('href')?.includes('#gallery')) {
        setTimeout(() => {
          const gallerySection = document.getElementById('gallery');
          if (gallerySection) {
            gallerySection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleClick);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'bromine', name: 'Bromine' },
    { id: 'magnesium', name: 'Magnesium Chloride' }
  ];

  const products = [
    {
      id: 1,
      name: 'Liquid Bromine',
      category: 'bromine',
      description: 'High-purity liquid bromine for industrial applications',
      features: [
        '99.5% minimum purity',
        'Industrial grade quality',
        'Corrosive liquid handling',
        'Bulk packaging available',
        'ISO certified production'
      ],
      specifications: {
        'Appearance': 'Reddish Brown Fuming Liquid',
        'Relative Density @27°C': '3.052',
        'Assay / Bromine, percent by mass, Min': '98.5',
        'Chlorine (as CL) percent by mass, Max': '0.5',
        'Non Volatile Matter, percent by mass, Max': '0.05',
        'Moisture Content, percent by mass, Max': '0.05',
        'Sulphates (As SO4) percent by mass, Max': '0.015'
      },
      applications: [
        'Chemical manufacturing',
        'Pharmaceutical industry',
        'Water treatment',
        'Flame retardants',
        'Agricultural chemicals'
      ],
      safety: [
        'Corrosive - handle with extreme care',
        'Use appropriate PPE',
        'Store in well-ventilated area',
        'Keep away from incompatible materials',
        'Follow MSDS guidelines'
      ],
      coa: {
        pdf: '/documents/bromine-coa.pdf',
        image: '/img/product/Bromine Certificate Of Analysis - Specifications.jpg'
      }
    },
    {
      id: 2,
      name: 'Magnesium Chloride Flakes',
      category: 'magnesium',
      description: 'High-purity magnesium chloride flakes for various applications',
      features: [
        '99% minimum purity',
        'Food grade available',
        'Multiple mesh sizes',
        'Bulk packaging options',
        'Consistent quality'
      ],
      specifications: {
        'Appearance': 'White crystalline flakes',
        'Assay (as MgCl2.6H2O)': '99% Min',
        'Chloride Content': '34.5% Min',
        'Magnesium Content': '11.8% Min',
        'Moisture Content': '0.5% Max',
        'Insoluble Matter': '0.1% Max'
      },
      applications: [
        'Food processing',
        'Dust control',
        'Road de-icing',
        'Textile industry',
        'Construction materials'
      ],
      safety: [
        'Low toxicity',
        'Store in dry conditions',
        'Avoid contact with eyes',
        'Use appropriate PPE',
        'Follow MSDS guidelines'
      ],
      coa: {
        pdf: '/documents/magnesium-chloride-coa.pdf',
        image: '/img/product/Magnesium Chloride Flakes 3.jpg'
      }
    }
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(product => product.category === activeCategory);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Products
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Premium quality chemical products for industrial and commercial applications
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap justify-center gap-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              {/* Product Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-3xl font-bold text-white mb-2">{product.name}</h2>
                    <p className="text-blue-100 text-lg">{product.description}</p>
                  </div>
                  <div className="hidden md:flex items-center space-x-4">
                    <div className="flex items-center text-blue-100">
                      <Shield className="w-5 h-5 mr-2" />
                      <span>ISO Certified</span>
                    </div>
                    <div className="flex items-center text-blue-100">
                      <Users className="w-5 h-5 mr-2" />
                      <span>Trusted by Industries</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Features */}
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Check className="w-6 h-6 mr-2 text-green-500" />
                    Key Features
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Specifications */}
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Beaker className="w-6 h-6 mr-2 text-blue-500" />
                    Technical Specifications
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                          <span className="font-medium text-gray-700 dark:text-gray-300">{key}</span>
                          <span className="text-gray-600 dark:text-gray-400">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Applications */}
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Users className="w-6 h-6 mr-2 text-purple-500" />
                    Applications
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {product.applications.map((application, index) => (
                      <div key={index} className="flex items-center">
                        <Check className="w-5 h-5 text-purple-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{application}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Safety Information */}
                <div className="mb-8">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Shield className="w-6 h-6 mr-2 text-orange-500" />
                    Safety Information
                  </h3>
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      {product.safety.map((safety, index) => (
                        <div key={index} className="flex items-start">
                          <Shield className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 dark:text-gray-300">{safety}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certificate of Analysis Section */}
      <div id="coa" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h2 className="text-3xl font-bold text-white mb-2 flex items-center">
              <FileText className="w-8 h-8 mr-3" />
              Certificate of Analysis (COA)
            </h2>
            <p className="text-blue-100 text-lg">
              Download and view our product certificates for quality assurance
            </p>
          </div>

          <div className="p-8">
            <div className="max-w-2xl mx-auto">
              {/* Bromine COA */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Bromine Certificate of Analysis
                </h3>
                
                {/* Clickable Image */}
                <div className="mb-4">
                  <div 
                    className="cursor-pointer group relative overflow-hidden rounded-lg"
                    onClick={() => setShowCOAModal(true)}
                  >
                    <img 
                      src="/img/product/Bromine Certificate Of Analysis - Specifications.jpg" 
                      alt="Bromine Certificate of Analysis" 
                      className="w-full h-auto rounded-lg transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white bg-opacity-90 rounded-full p-3">
                        <Eye className="w-6 h-6 text-gray-700" />
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mt-2">
                    Click to view full size
                  </p>
                </div>

                {/* Download Options */}
                <div className="space-y-3">
                  {/* <a
                    href="/img/product/Bromine Certificate Of Analysis - Specifications.jpg"
                    download
                    className="flex items-center justify-center w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download COA Image
                  </a> */}
                  <button
                    onClick={() => setShowPDFModal(true)}
                    className="flex items-center justify-center w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Eye className="w-5 h-5 mr-2" />
                    View PDF Certificate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Gallery */}
      <ProductGallery />

      {/* COA Modal */}
      {showCOAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl w-full max-h-[90vh]">
            <button
              onClick={() => setShowCOAModal(false)}
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden max-h-[90vh]">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Bromine Certificate of Analysis
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  Detailed specifications and quality parameters
                </p>
              </div>
              
              <div className="relative flex-1 min-h-0 p-6">
                <img
                  src="/img/product/Bromine Certificate Of Analysis - Specifications.jpg"
                  alt="Bromine Certificate of Analysis"
                  className="w-full h-auto object-contain bg-gray-50 dark:bg-gray-900 rounded-lg"
                  style={{ maxHeight: '70vh' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {showPDFModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-6xl w-full max-h-[90vh]">
            <button
              onClick={() => setShowPDFModal(false)}
              className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
            </button>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden max-h-[90vh]">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Bromine Certificate of Analysis - PDF
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  View the complete PDF certificate
                </p>
              </div>
              
              <div className="relative flex-1 min-h-0 p-6">
                <iframe
                  src="/Tatvam Chemicals Bromine COA.pdf#toolbar=0&navpanes=0&scrollbar=0"
                  className="w-full h-full min-h-[70vh] rounded-lg border-0"
                  title="Bromine Certificate of Analysis PDF"
                  style={{ backgroundColor: 'white' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products; 